// Built-ins
import { useState, useEffect, useRef } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/bootstrap.css';

// Global Variables
import { headers } from "../../global_vars";
import { useMergedModalVisibility } from "../../common/useMergedModalVisibility";
import { NESTED_MODAL_PROPS } from "../../common/nestedModalProps";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AutocompleteSelect from "../../common/AutocompleteSelect";
import AddProfessionModal from "./add_profession";
import AddCityModal from "./add_city";
import {
  formatPhoneForApi,
  isOptionalPhoneValid,
  isRequiredPhoneValid,
} from "../../../utils/phoneInput";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi } from "../../../utils/richText";
import { cityOptionLabel } from "../../common/cityOptionLabel";

// Variables
window.Swal = Swal;

// API endpoints
const ADD_PROFESSIONAL = "https://ultima.icsgr.com/api/data_management/professionals/";
const ALL_PROFESSIONS = "https://ultima.icsgr.com/api/administration/all_professions/";
const ALL_CITIES = "https://ultima.icsgr.com/api/regions/all_cities/";

function AddProfessionalModal({
  onProfessionalCreated,
  defaultProfessionId,
  lockProfession = false,
  prefill = null,
  show: showProp,
  onHide: onHideProp,
  hideTrigger = false,
  nested = false,
}) {
  const { show, open, close } = useMergedModalVisibility(showProp, onHideProp);
  const [addProfessionOpen, setAddProfessionOpen] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);

  // Form state
  const [fullname, setFullname] = useState("");
  const [profession_id, setProfession_id] = useState(defaultProfessionId || "");
  const [city_id, setCity_id] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [reliability, setReliability] = useState("");
  const [afm, setAfm] = useState("");
  const [bankDetails, setBankDetails] = useState("");
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");

  // Dropdown data
  const [professions, setProfessions] = useState([]);
  const [cities, setCities] = useState([]);

  const resetForm = () => {
    setFullname("");
    setProfession_id("");
    setCity_id("");
    setAddress("");
    setEmail("");
    setPhone("");
    setMobile("");
    setReliability("");
    setAfm("");
    setBankDetails("");
    setActive(true);
    setNotes("");
  };

  const handleClose = () => {
    close();
    resetForm();
  };
  const handleShow = () => {
    resetForm();
    if (defaultProfessionId) setProfession_id(defaultProfessionId);
    open();
  };

  const prefillAppliedRef = useRef(false);

  useEffect(() => {
    if (!show) {
      prefillAppliedRef.current = false;
      return;
    }
    if (!prefill || prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;
    if (prefill.fullname) setFullname(prefill.fullname);
    if (prefill.email) setEmail(prefill.email);
    if (prefill.address) setAddress(prefill.address);
    if (prefill.phone) setPhone(prefill.phone);
    if (prefill.mobile) setMobile(prefill.mobile);
    if (prefill.reliability) setReliability(prefill.reliability);
    if (prefill.notes) setNotes(prefill.notes);
    if (prefill.afm) setAfm(prefill.afm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show || !prefill) return;
    if (!profession_id && prefill.professionTitle && professions.length) {
      const match = professions.find((p) => p.title === prefill.professionTitle);
      if (match) setProfession_id(match.profession_id);
    }
    if (!city_id && prefill.cityTitle && cities.length) {
      const title = prefill.cityTitle;
      const match =
        cities.find((c) => c.title === title) ||
        cities.find((c) => c.title && title.includes(c.title) && c.title.length >= 3);
      if (match) setCity_id(match.city_id);
    }
  }, [show, prefill, professions, cities, profession_id, city_id]);

  useEffect(() => {
    if (!show) return;
    if (defaultProfessionId && (lockProfession || !profession_id)) {
      setProfession_id(defaultProfessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultProfessionId, lockProfession]);

  // Load dropdown data when modal opens
  useEffect(() => {
    if (show) {
      loadDropdownData();
    }
  }, [show]);

  const loadDropdownData = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const professionsRes = await axios.get(ALL_PROFESSIONS, { headers: currentHeaders });
      setProfessions(professionsRes.data.all_professions || []);

      const citiesRes = await axios.get(ALL_CITIES, { headers: currentHeaders });
      setCities(citiesRes.data.all_cities || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  // Validation
  const isFullnameValid = fullname.trim().length >= 2 && fullname.trim().length <= 40;
  const isProfessionValid = profession_id !== "";
  const isCityValid = city_id !== "";
  const isEmailValid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPhoneValid = isOptionalPhoneValid(phone);
  const isMobileValid = isRequiredPhoneValid(mobile);
  const isAfmValid = !afm || (afm.length === 9 && /^\d{9}$/.test(afm));
  const isFormValid = isFullnameValid && isProfessionValid && isCityValid && isMobileValid && isEmailValid && isPhoneValid && isAfmValid;

  const createNewProfessional = async () => {
    if (!isFormValid) return;
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const res = await axios.post(
        ADD_PROFESSIONAL,
        {
          fullname: fullname.trim(),
          profession_id,
          city_id,
          address: address.trim() || null,
          email: email.trim() || null,
          phone: formatPhoneForApi(phone),
          mobile: formatPhoneForApi(mobile),
          reliability: reliability || null,
          afm: afm.trim() || null,
          bank_details: htmlForApi(bankDetails),
          active,
          notes: htmlForApi(notes),
        },
        { headers: currentHeaders }
      );
      const created = res?.data;
      handleClose();
      if (typeof onProfessionalCreated === "function") {
        try {
          await Promise.resolve(onProfessionalCreated(created));
        } catch (_) {}
      } else {
        Swal.fire({ icon: "success", title: "Success", text: "Professional created successfully!" });
      }
    } catch (error) {
      console.error("Error creating professional:", error);
      let apiMsg = "Failed to create professional";
      const data = error.response?.data;
      if (data?.error) apiMsg = data.error;
      else if (data?.fullname) apiMsg = data.fullname[0];
      else if (data?.profession_id) apiMsg = data.profession_id[0];
      else if (data?.city_id) apiMsg = data.city_id[0];
      else if (data?.mobile) apiMsg = data.mobile[0];
      else if (data?.email) apiMsg = data.email[0];
      else if (data?.phone) apiMsg = data.phone[0];
      else if (data?.detail) apiMsg = data.detail;
      Swal.fire("Error", apiMsg, "error");
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
          <BiPlus style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }} />
          Create new Professional
        </Button>
      )}

      <GuardedModal
        show={show}
        onHide={handleClose}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        enforceFocus={false}
        {...(nested ? NESTED_MODAL_PROPS : {})}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">Create new Professional</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <h6 className="mb-2">Basic Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Full name *:</Form.Label>
                      <Form.Control
                        maxLength={40}
                        placeholder="e.g., John Smith"
                        onChange={(e) => setFullname(e.target.value)}
                        value={fullname}
                        isInvalid={fullname !== "" && !isFullnameValid}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2 mt-3">Links</h6>
                <Row>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-2"
                      label="Profession"
                      required
                      value={profession_id}
                      onChange={setProfession_id}
                      disabled={lockProfession}
                      isInvalid={profession_id !== "" && !isProfessionValid}
                      placeholderOption="Select Profession"
                      onAddClick={lockProfession ? undefined : () => setAddProfessionOpen(true)}
                      overviewLinkEntity="profession"
                    >
                      {Array.isArray(professions) && professions.map((p) => (
                        <option key={p.profession_id} value={p.profession_id} title={p.profession_id}>{p.title}</option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-2"
                      label="City"
                      required
                      value={city_id}
                      onChange={setCity_id}
                      isInvalid={city_id !== "" && !isCityValid}
                      placeholderOption="Select City"
                      onAddClick={() => setAddCityOpen(true)}
                      overviewLinkEntity="city"
                    >
                      {Array.isArray(cities) && cities.map((c) => (
                        <option key={c.city_id} value={c.city_id} title={c.city_id}>{cityOptionLabel(c)}</option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                </Row>

                <h6 className="mb-2 mt-3">Contact</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Address:</Form.Label>
                      <Form.Control onChange={(e) => setAddress(e.target.value)} value={address} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>E-mail:</Form.Label>
                      <Form.Control type="email" placeholder="name@example.com" onChange={(e) => setEmail(e.target.value)} value={email} isInvalid={email !== "" && !isEmailValid} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telephone:</Form.Label>
                      <PhoneInput
                        country={'gr'}
                        value={phone}
                        onChange={(val) => setPhone(val)}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ name: 'phone' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cell phone *:</Form.Label>
                      <PhoneInput
                        country={'gr'}
                        value={mobile}
                        onChange={(val) => setMobile(val)}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ name: 'mobile' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2 mt-3">Tax &amp; Banking</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>AFM:</Form.Label>
                      <Form.Control
                        maxLength={9}
                        placeholder="e.g., 123456789"
                        onChange={(e) => setAfm(e.target.value.replace(/\D/g, "").slice(0, 9))}
                        value={afm}
                        isInvalid={afm !== "" && !isAfmValid}
                      />
                      <Form.Text className="text-muted">Must be exactly 9 digits</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-2">
                  <Form.Label>Bank details:</Form.Label>
                  <RichTextEditor
                    rows={3}
                    value={bankDetails}
                    onChange={setBankDetails}
                    placeholder="e.g., Bank name, IBAN, account holder"
                  />
                </Form.Group>

                <h6 className="mb-2 mt-3">Status</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reliability:</Form.Label>
                      <AutocompleteSelect value={reliability} onChange={setReliability} placeholder="Select">
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check type="switch" id="active-switch" label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2 mt-3">Notes</h6>
                <Form.Group className="mb-2">
                  <RichTextEditor
                    rows={3}
                    value={notes}
                    onChange={setNotes}
                    placeholder="Optional notes about the professional"
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "red" }}>
                {!isFullnameValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Full name is required (2–40 chars).</li>
                )}
                {!isProfessionValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Profession is required.</li>
                )}
                {!isCityValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> City is required.</li>
                )}
                {!isMobileValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Cell phone is required (max 15 chars).</li>
                )}
                {!isEmailValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> E-mail must be a valid e-mail address.</li>
                )}
                {!isPhoneValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Telephone must be 15 characters or less.</li>
                )}
                {!isAfmValid && (
                  <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> AFM must be exactly 9 digits.</li>
                )}
              </ul>
            ) : (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "green" }}>
                <li><AiOutlineCheckCircle style={{ fontSize: 18, marginRight: 6 }} /> Validated</li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>Close</Button>
          <Button color="green" onClick={createNewProfessional} disabled={!isFormValid}>Save Changes</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddProfessionModal
        nested
        stackLevel={nested ? 2 : 1}
        hideTrigger
        show={addProfessionOpen}
        onHide={() => setAddProfessionOpen(false)}
        onProfessionCreated={async (data) => {
          await loadDropdownData();
          const id = data?.profession_id;
          if (id) setProfession_id(id);
          setAddProfessionOpen(false);
        }}
      />
      <AddCityModal
        nested
        stackLevel={nested ? 2 : 1}
        hideTrigger
        show={addCityOpen}
        onHide={() => setAddCityOpen(false)}
        onCityCreated={async (data) => {
          await loadDropdownData();
          const id = data?.city_id;
          if (id) setCity_id(id);
          setAddCityOpen(false);
        }}
      />
    </>
  );
}

export default AddProfessionalModal;
